// Conversation tab: send a published form to this customer, and see what they were sent and what
// they submitted. The conversation and contact come from the dashboard context; the server checks
// both against the verified account.

import { useHostActions, useHostContext } from '@waforms/app-sdk/client';
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Select,
  Spinner,
  Tabs,
  Textarea,
  useToast,
} from '@waforms/ui';
import { ChevronDown, ClipboardList, MessageSquareText, Send } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import type {
  ConversationFormsDto,
  SendDto,
  SendableFormDto,
  SubmissionDto,
} from '../../shared/api-types.ts';
import { formatAnswer } from '../../shared/answers.ts';
import { charCount, LIMITS } from '../../shared/limits.ts';
import { Callout, CharCount } from '../components/common.tsx';
import { ApiError, useContactActivity, useConversationForms, useSendForm } from '../lib/api.ts';
import { dateTime, timeAgo } from '../lib/format.ts';

export function ConversationTab() {
  const host = useHostContext();
  const conversationId = host.conversation?.id;
  const contactId = host.contact?.id;

  if (!conversationId || !contactId) {
    return (
      <EmptyState
        icon={<MessageSquareText />}
        title="No conversation here"
        description="Open this tab from a conversation to send a form to the customer."
      />
    );
  }
  return <ConversationPanel conversationId={conversationId} contactId={contactId} />;
}

function ConversationPanel({ conversationId, contactId }: { conversationId: number; contactId: number }) {
  const forms = useConversationForms(conversationId);

  return (
    <div className="flex flex-col gap-5 p-4">
      {forms.isPending ? (
        <div className="flex justify-center py-6 text-n-slate-11">
          <Spinner label="Loading forms" />
        </div>
      ) : forms.isError ? (
        <p role="alert" className="m-0 text-body-main text-n-ruby-11">
          Could not load forms: {forms.error.message}
        </p>
      ) : (
        <SendPanel data={forms.data} conversationId={conversationId} contactId={contactId} />
      )}
      <History contactId={contactId} conversationId={conversationId} />
    </div>
  );
}

export function SendPanel({
  data,
  conversationId,
  contactId,
}: {
  data: ConversationFormsDto;
  conversationId: number;
  contactId: number;
}) {
  if (!data.inbox) {
    return (
      <EmptyState
        className="py-6"
        icon={<ClipboardList />}
        title="Forms work on WhatsApp"
        description="This conversation is not on a WhatsApp Cloud inbox, so forms cannot be sent here."
      />
    );
  }
  if (data.forms.length === 0) {
    return (
      <EmptyState
        className="py-6"
        icon={<ClipboardList />}
        title="No published forms"
        description={`Forms for ${data.inbox.name} appear here once an administrator publishes them in the Forms app.`}
      />
    );
  }
  return <SendForm data={data} conversationId={conversationId} contactId={contactId} />;
}

function SendForm({
  data,
  conversationId,
  contactId,
}: {
  data: ConversationFormsDto;
  conversationId: number;
  contactId: number;
}) {
  const [formId, setFormId] = useState(data.forms[0]!.id);
  const selected = data.forms.find((form) => form.id === formId) ?? data.forms[0]!;
  return (
    <section aria-label="Send a form" className="flex flex-col gap-3">
      <h2 className="m-0 text-heading-2 text-n-slate-12">Send a form</h2>
      {!data.conversation.canReply && (
        <Callout tone="warning" title="Outside the 24-hour window">
          The customer last wrote more than 24 hours ago, so WhatsApp only allows approved templates here.
          Send a template with a FLOW button that uses the form; its Form ID is shown on the form’s page in
          the Forms app.
        </Callout>
      )}
      <FormField label="Form">
        <Select
          size="sm"
          value={selected.id}
          onChange={(event) => setFormId(event.target.value)}
          options={data.forms.map((form) => ({
            value: form.id,
            label: form.lifecycle === 'blocked' ? `${form.name} (blocked by Meta)` : form.name,
          }))}
        />
      </FormField>
      {/* Remount per form so the message starts from that form's defaults. */}
      <MessageEditor
        key={selected.id}
        form={selected}
        conversationId={conversationId}
        contactId={contactId}
        canReply={data.conversation.canReply}
      />
    </section>
  );
}

function MessageEditor({
  form,
  conversationId,
  contactId,
  canReply,
}: {
  form: SendableFormDto;
  conversationId: number;
  contactId: number;
  canReply: boolean;
}) {
  const [body, setBody] = useState(form.defaults.body);
  const [cta, setCta] = useState(form.defaults.cta);
  const [header, setHeader] = useState(form.defaults.header ?? '');
  const [footer, setFooter] = useState(form.defaults.footer ?? '');
  const [more, setMore] = useState(Boolean(form.defaults.header || form.defaults.footer));
  const [error, setError] = useState<string | null>(null);
  const send = useSendForm(conversationId, contactId);
  const actions = useHostActions();
  const { toast } = useToast();

  const invalid =
    !body.trim() ||
    !cta.trim() ||
    charCount(body.trim()) > LIMITS.messageBody ||
    charCount(cta.trim()) > LIMITS.flowCta ||
    charCount(header.trim()) > LIMITS.messageHeader ||
    charCount(footer.trim()) > LIMITS.messageFooter;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (invalid) return;
    setError(null);
    send.mutate(
      {
        formId: form.id,
        body: body.trim(),
        cta: cta.trim(),
        header: header.trim() || null,
        footer: footer.trim() || null,
      },
      {
        onSuccess: () => actions.toast(`“${form.name}” sent`),
        onError: (failure) => {
          const message = failure instanceof ApiError ? failure.message : 'Sending failed. Try again.';
          setError(message);
          if (!(failure instanceof ApiError) || failure.status >= 500) {
            toast({ title: 'Could not send the form', description: message, tone: 'error' });
          }
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <FormField
        label={
          <span className="flex items-center justify-between">
            Message <CharCount value={body} max={LIMITS.messageBody} />
          </span>
        }
      >
        <Textarea rows={3} value={body} onChange={(event) => setBody(event.target.value)} />
      </FormField>
      <FormField
        label={
          <span className="flex items-center justify-between">
            Button <CharCount value={cta} max={LIMITS.flowCta} />
          </span>
        }
      >
        <Input size="sm" value={cta} onChange={(event) => setCta(event.target.value)} />
      </FormField>
      <button
        type="button"
        aria-expanded={more}
        onClick={() => setMore(!more)}
        className="inline-flex cursor-pointer items-center gap-1 self-start border-0 bg-transparent p-0 text-label-small text-n-slate-11"
      >
        <ChevronDown className={more ? 'size-3.5 rotate-180' : 'size-3.5'} aria-hidden="true" /> Header and
        footer
      </button>
      {more && (
        <div className="grid grid-cols-1 gap-3">
          <FormField
            label={
              <span className="flex items-center justify-between">
                Header <CharCount value={header} max={LIMITS.messageHeader} />
              </span>
            }
          >
            <Input
              size="sm"
              value={header}
              placeholder="Optional"
              onChange={(event) => setHeader(event.target.value)}
            />
          </FormField>
          <FormField
            label={
              <span className="flex items-center justify-between">
                Footer <CharCount value={footer} max={LIMITS.messageFooter} />
              </span>
            }
          >
            <Input
              size="sm"
              value={footer}
              placeholder="Optional"
              onChange={(event) => setFooter(event.target.value)}
            />
          </FormField>
        </div>
      )}

      {/* What the customer gets: a message with a button that opens the form. */}
      <div
        aria-hidden="true"
        className="flex flex-col overflow-hidden rounded-xl bg-n-alpha-1 text-body-main"
      >
        <div className="flex flex-col gap-1 px-3 py-2">
          {header.trim() && <span className="font-semibold text-n-slate-12">{header.trim()}</span>}
          <span className="whitespace-pre-wrap text-n-slate-12">{body.trim() || '…'}</span>
          {footer.trim() && <span className="text-label-small text-n-slate-10">{footer.trim()}</span>}
        </div>
        <span className="border-t border-n-weak py-2 text-center font-medium text-n-blue-11">
          {cta.trim() || 'Open'}
        </span>
      </div>

      {error && (
        <p role="alert" className="m-0 text-body-main text-n-ruby-11">
          {error}
        </p>
      )}
      <Button
        type="submit"
        icon={<Send className="size-4" />}
        loading={send.isPending}
        disabled={invalid || !canReply}
      >
        Send form
      </Button>
    </form>
  );
}

function History({ contactId, conversationId }: { contactId: number; conversationId: number }) {
  const [tab, setTab] = useState<'submissions' | 'sends'>('submissions');
  const activity = useContactActivity(contactId, conversationId);

  return (
    <section aria-label="This contact's forms" className="flex flex-col gap-3 border-t border-n-weak pt-4">
      <Tabs
        aria-label="History"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'submissions', label: 'Submitted', count: activity.data?.submissions.length },
          { value: 'sends', label: 'Sent', count: activity.data?.sends.length },
        ]}
      />
      {activity.isPending ? (
        <div className="flex justify-center py-4 text-n-slate-11">
          <Spinner label="Loading history" />
        </div>
      ) : activity.isError ? (
        <p role="alert" className="m-0 text-body-main text-n-ruby-11">
          Could not load the history: {activity.error.message}
        </p>
      ) : tab === 'submissions' ? (
        activity.data.submissions.length === 0 ? (
          <p className="m-0 text-body-main text-n-slate-11">This contact has not submitted a form yet.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {activity.data.submissions.map((submission) => (
              <SubmissionItem
                key={submission.id}
                submission={submission}
                currentConversation={conversationId}
              />
            ))}
          </ol>
        )
      ) : activity.data.sends.length === 0 ? (
        <p className="m-0 text-body-main text-n-slate-11">No forms were sent to this contact yet.</p>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {activity.data.sends.map((send) => (
            <SendItem key={send.id} send={send} currentConversation={conversationId} />
          ))}
        </ol>
      )}
    </section>
  );
}

function SubmissionItem({
  submission,
  currentConversation,
}: {
  submission: SubmissionDto;
  currentConversation: number;
}) {
  const [open, setOpen] = useState(false);
  const actions = useHostActions();
  const elsewhere = submission.conversationId !== null && submission.conversationId !== currentConversation;
  return (
    <li className="flex flex-col gap-1.5 rounded-xl bg-n-solid-2 px-3 py-2.5 outline outline-1 -outline-offset-1 outline-n-container">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex cursor-pointer items-center justify-between gap-2 border-0 bg-transparent p-0 text-start"
      >
        <span className="truncate text-heading-3 text-n-slate-12">{submission.formName ?? 'Form'}</span>
        <time
          dateTime={submission.submittedAt}
          title={dateTime(submission.submittedAt)}
          className="shrink-0 text-label-small text-n-slate-10"
        >
          {timeAgo(submission.submittedAt)}
        </time>
      </button>
      <div className="flex flex-wrap gap-1">
        {submission.duplicate && <Badge color="amber">Duplicate</Badge>}
        {submission.source === 'template' && <Badge>From template</Badge>}
      </div>
      {open && (
        <dl className="m-0 flex flex-col gap-1.5">
          {submission.answers.map((answer) => (
            <div key={answer.fieldId}>
              <dt className="text-label-small text-n-slate-11">{answer.label}</dt>
              <dd className="m-0 whitespace-pre-wrap break-words text-body-main text-n-slate-12">
                {answer.value === null ? (
                  <span className="text-n-slate-9">-</span>
                ) : (
                  formatAnswer(answer.value)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {elsewhere && (
        <Button
          size="xs"
          variant="link"
          className="self-start"
          onClick={() => actions.navigate('conversation', submission.conversationId!)}
        >
          Open conversation #{submission.conversationId}
        </Button>
      )}
    </li>
  );
}

function SendItem({ send, currentConversation }: { send: SendDto; currentConversation: number }) {
  const actions = useHostActions();
  return (
    <li className="flex flex-col gap-1 rounded-xl bg-n-solid-2 px-3 py-2.5 outline outline-1 -outline-offset-1 outline-n-container">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-heading-3 text-n-slate-12">{send.formName}</span>
        <time
          dateTime={send.createdAt}
          title={dateTime(send.createdAt)}
          className="shrink-0 text-label-small text-n-slate-10"
        >
          {timeAgo(send.createdAt)}
        </time>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-label-small text-n-slate-11">
        {send.status === 'failed' ? (
          <Badge color="ruby" title={send.error ?? undefined}>
            Not delivered
          </Badge>
        ) : send.submitted ? (
          <Badge color="teal">Submitted</Badge>
        ) : (
          <Badge>Waiting</Badge>
        )}
        <span>by {send.sentByName}</span>
      </div>
      {send.status === 'failed' && send.error && (
        <p className="m-0 text-label-small text-n-ruby-11">{send.error}</p>
      )}
      {send.conversationId !== currentConversation && (
        <Button
          size="xs"
          variant="link"
          className="self-start"
          onClick={() => actions.navigate('conversation', send.conversationId)}
        >
          Open conversation #{send.conversationId}
        </Button>
      )}
    </li>
  );
}
